// src/common/verification/batch-verify.js
import { extractErrorMessage } from '../utils/error-utils.js';

const STATUS_LABELS = {
  queued: 'Queued',
  parsing: 'Parsing…',
  resolving: 'Resolving…',
  analyzing: 'Analyzing…',
  done: 'Done',
  error: 'Error'
};

function ensureBatchRow(cardEl) {
  if (!cardEl) return null;
  const body = cardEl.querySelector('.card-body');
  if (!body) return null;

  let rowEl = body.querySelector('.batch-verify-row');
  if (rowEl) {
    return {
      rowEl,
      statusEl: rowEl.querySelector('.batch-verify-status'),
      detailEl: rowEl.querySelector('.batch-verify-detail'),
      cancelBtn: rowEl.querySelector('.batch-verify-cancel'),
      retryBtn: rowEl.querySelector('.batch-verify-retry')
    };
  }

  rowEl = document.createElement('div');
  rowEl.className = 'row batch-verify-row';

  const statusEl = document.createElement('span');
  statusEl.className = 'batch-verify-status';

  const detailEl = document.createElement('span');
  detailEl.className = 'batch-verify-detail';

  const spacer = document.createElement('span');
  spacer.className = 'spacer';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn-secondary btn-small batch-verify-cancel';
  cancelBtn.textContent = 'Cancel';

  const retryBtn = document.createElement('button');
  retryBtn.type = 'button';
  retryBtn.className = 'btn-secondary btn-small batch-verify-retry';
  retryBtn.textContent = 'Retry';
  retryBtn.hidden = true;

  rowEl.append(statusEl, detailEl, spacer, cancelBtn, retryBtn);

  const askContainer = body.querySelector('.ask-container');
  if (askContainer) {
    body.insertBefore(rowEl, askContainer);
  } else {
    body.appendChild(rowEl);
  }

  return { rowEl, statusEl, detailEl, cancelBtn, retryBtn };
}

function formatStatus(status, detail) {
  const label = STATUS_LABELS[status] || STATUS_LABELS.queued;
  if (!detail) return label;
  return `${label} — ${detail}`;
}

export function createBatchVerificationQueue() {
  // In the normal footnote list UI, we don't want extra status rows under cards.
  // Only enable this UI in explicit batch/queue mode.
  const enabled = typeof window !== 'undefined' && !!window.__FC_BATCH_MODE__;
  if (!enabled) {
    return {
      registerRow: () => {},
      updateRowStatus: () => {},
      isCancelled: () => false,
    };
  }

  const rows = new Map();
  const cancelled = new Set();

  const registerRow = (rowId, { cardEl, onCancel, onRetry } = {}) => {
    const key = String(rowId);
    const ui = ensureBatchRow(cardEl);
    if (!ui) return;

    if (!rows.has(key)) {
      rows.set(key, {
        status: 'queued',
        detail: null,
        ui,
        onCancel,
        onRetry
      });
    }

    const rowState = rows.get(key);

    ui.cancelBtn.onclick = () => {
      cancelled.add(key);
      if (rowState) {
        rowState.status = 'error';
        rowState.detail = 'Cancelled';
      }
      if (typeof rowState?.onCancel === 'function') {
        rowState.onCancel();
      }
      updateRowStatus(key, 'error', 'Cancelled');
    };

    ui.retryBtn.onclick = async () => {
      cancelled.delete(key);
      updateRowStatus(key, 'queued', 'Retrying');
      if (typeof rowState?.onRetry === 'function') {
        try {
          await rowState.onRetry();
        } catch (error) {
          updateRowStatus(key, 'error', extractErrorMessage(error));
        }
      }
    };

    updateRowStatus(key, rowState?.status || 'queued', rowState?.detail || null);
  };

  const updateRowStatus = (rowId, status, detail = null) => {
    const key = String(rowId);
    const rowState = rows.get(key);
    if (!rowState) return;

    rowState.status = status;
    rowState.detail = detail;

    const { rowEl, statusEl, detailEl, cancelBtn, retryBtn } = rowState.ui || {};
    if (!rowEl || !statusEl || !cancelBtn || !retryBtn) return;

    statusEl.textContent = formatStatus(status, detail);
    if (detailEl) {
      detailEl.textContent = '';
    }

    rowEl.dataset.status = status;

    if (status === 'done') {
      cancelBtn.hidden = true;
      retryBtn.hidden = true;
    } else if (status === 'error') {
      cancelBtn.hidden = true;
      retryBtn.hidden = false;
    } else {
      cancelBtn.hidden = false;
      retryBtn.hidden = true;
    }
  };

  const isCancelled = (rowId) => cancelled.has(String(rowId));

  return {
    registerRow,
    updateRowStatus,
    isCancelled
  };
}
